import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../core/context/AuthContext";
import {
  readConnectorSnapshot,
  writeConnectorSnapshot,
} from "../../core/utils/connectorSnapshotCache";
import { getAllActionItems, getBusinessWorkflows } from "../businessModeService";
import {
  buildActionItemsSourceNav,
  compareActionItemsStable,
} from "./actionItemHelpers";
import {
  mergeItemsPreservingPendingMutations,
  reuseUnchangedItems,
} from "./shared/optimisticActionItemStatus";
import { isWorkflowSyncingActionItems } from "./shared/workflowStatus";
import { DEFAULT_ACTION_ITEM_CATEGORY_CATALOG } from "./actionItemCategories";

const ActionItemsSidebarContext = createContext(null);

/**
 * Shared sidebar state for the global action-items page (search + source nav counts).
 *
 * @example
 * const { searchQuery, setSearchQuery } = useActionItemsSidebar();
 */
export const useActionItemsSidebar = () => {
  const ctx = useContext(ActionItemsSidebarContext);
  if (!ctx) {
    throw new Error("useActionItemsSidebar must be used within ActionItemsSidebarProvider");
  }
  return ctx;
};

/**
 * Optional hook for pages outside the provider (e.g. settings) — returns null when absent.
 */
export const useActionItemsSidebarOptional = () => useContext(ActionItemsSidebarContext);

const sameJson = (a, b) => {
  if (a === b) return true;
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch (_) {
    return false;
  }
};

/**
 * Per-workflow fingerprint used to notice that connector state changed under the task list:
 * a sync that just finished, a new `last_synced_at`, a connector added or removed.
 * (A sync merely *starting* is deliberately not a change: nothing to refetch yet.)
 *
 * @example
 * buildWorkflowChangeSignature([{ workflow_id: "wf-1", last_synced_at: "2026-10-29T10:00:00Z" }]);
 * // Map { "wf-1" => { syncing: false, lastSyncedAt: "2026-10-29T10:00:00Z" } }
 */
export const buildWorkflowChangeSignature = (workflows) => {
  const signature = new Map();
  (Array.isArray(workflows) ? workflows : []).forEach((workflow) => {
    const id = String(workflow?.workflow_id || "").trim();
    if (!id) return;
    signature.set(id, {
      syncing: isWorkflowSyncingActionItems(workflow),
      lastSyncedAt: String(workflow?.last_synced_at ?? ""),
    });
  });
  return signature;
};

/**
 * True when the task list should be refetched because connector state moved.
 *
 * @example
 * didWorkflowStateChange(
 *   new Map([["wf-1", { syncing: true, lastSyncedAt: "" }]]),
 *   new Map([["wf-1", { syncing: false, lastSyncedAt: "" }]])
 * );
 * // true (sync finished, even if it added nothing and left last_synced_at alone)
 */
export const didWorkflowStateChange = (previous, next) => {
  if (!previous || !next) return false;
  if (previous.size !== next.size) return true;
  for (const [id, now] of next) {
    const before = previous.get(id);
    if (!before) return true;
    if (before.syncing && !now.syncing) return true;
    if (before.lastSyncedAt !== now.lastSyncedAt) return true;
  }
  return false;
};

const EMPTY_SOURCE_NAV = {
  totalCount: 0,
  manualCount: 0,
  hasManualItems: false,
  workflowEntries: [],
};

/**
 * @example
 * <ActionItemsSidebarProvider><Outlet /></ActionItemsSidebarProvider>
 *
 * @example
 * <ActionItemsSidebarProvider demoSeed={{ items, workflows }} />
 * // Skips API fetch; uses seeded rows for the static landing-page demo.
 */
export const ActionItemsSidebarProvider = ({ children, demoSeed = null }) => {
  const isDemo = Boolean(demoSeed);
  const navigate = useNavigate();
  const auth = useAuth();
  // Cache key. Never share a snapshot across accounts.
  const userKey = String(auth?.user?.id || auth?.user?.email || "").trim();
  // Last persisted connector list for this user: lets Settings paint instantly while the
  // live fetch runs. Read once per user; live data replaces it as soon as it lands.
  const connectorSnapshot = useMemo(
    () => (isDemo ? null : readConnectorSnapshot(userKey)),
    [isDemo, userKey]
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [sourceNav, setSourceNav] = useState(EMPTY_SOURCE_NAV);
  const [items, setItems] = useState(() =>
    Array.isArray(demoSeed?.items) ? demoSeed.items : []
  );
  const [workflows, setWorkflows] = useState(() =>
    Array.isArray(demoSeed?.workflows) ? demoSeed.workflows : []
  );
  // True once `workflows` came from the server this session (not just seeded/cached).
  const [workflowsLive, setWorkflowsLive] = useState(isDemo);
  const [workflowsRefreshing, setWorkflowsRefreshing] = useState(false);
  const workflowsInFlightRef = useRef(null);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [itemsError, setItemsError] = useState("");
  const [lastFetchedAt, setLastFetchedAt] = useState(isDemo ? Date.now() : null);
  const [categoryCatalog, setCategoryCatalog] = useState(
    DEFAULT_ACTION_ITEM_CATEGORY_CATALOG
  );
  const refreshInFlightRef = useRef(null);
  // When a refresh is requested while one is already in-flight, set this flag so a
  // follow-up refresh runs immediately after the current one completes.
  const refreshQueuedRef = useRef(false);
  // Tracks stableIds with in-flight optimistic mutations so polling never overwrites them.
  const pendingMutationIds = useRef(new Set());
  // After a mutation finishes, keep protecting that row briefly so an in-flight silent
  // refresh that started *before* the write cannot paint stale server state (pin flicker).
  const mutationCooldownUntilMs = useRef(new Map());
  const MUTATION_COOLDOWN_MS = 3000;
  const initialFetchRef = useRef(false);
  // Demo early-return reads current rows without putting them on the callback
  // identity. If `items`/`workflows` were useCallback deps, every successful
  // fetch would recreate `refreshActionItemsData` and retrigger NavSync.
  const itemsRef = useRef(items);
  const workflowsRef = useRef(workflows);
  itemsRef.current = items;
  workflowsRef.current = workflows;

  const getProtectedMutationIds = useCallback(() => {
    const now = Date.now();
    const protectedIds = new Set(pendingMutationIds.current);
    for (const [id, untilMs] of [...mutationCooldownUntilMs.current.entries()]) {
      if (untilMs > now) {
        protectedIds.add(id);
      } else {
        mutationCooldownUntilMs.current.delete(id);
      }
    }
    return protectedIds;
  }, []);

  useEffect(() => {
    setSourceNav(
      buildActionItemsSourceNav({
        items,
        workflows,
      })
    );
  }, [items, workflows]);

  /**
   * Refetch only the connector list (cheap; the task list can be thousands of rows).
   * Concurrent callers share one request. Unchanged payloads keep the same array identity
   * so effects keyed on `workflows` do not re-fire on every poll.
   *
   * @example
   * const list = await refreshWorkflows();
   * // [{ workflow_id: "wf-1", ... }]
   */
  const refreshWorkflows = useCallback(async () => {
    if (isDemo) return workflowsRef.current;
    if (workflowsInFlightRef.current) return workflowsInFlightRef.current;
    setWorkflowsRefreshing(true);
    const request = (async () => {
      try {
        const data = await getBusinessWorkflows();
        const list = Array.isArray(data) ? data : [];
        setWorkflows((prev) => (sameJson(prev, list) ? prev : list));
        setWorkflowsLive(true);
        return list;
      } finally {
        setWorkflowsRefreshing(false);
      }
    })();
    workflowsInFlightRef.current = request;
    try {
      return await request;
    } finally {
      if (workflowsInFlightRef.current === request) {
        workflowsInFlightRef.current = null;
      }
    }
  }, [isDemo]);

  /**
   * Load or refresh action items and workflows. Uses stale-while-revalidate when cached data exists.
   *
   * @example
   * // Input — cold load
   * await refreshActionItemsData({ silent: false });
   * // Output: fetches API, sets items/workflows, returns { items, workflows }
   *
   * @example
   * // Input — background refresh with cached rows visible
   * await refreshActionItemsData({ silent: true });
   */
  const refreshActionItemsData = useCallback(async ({ silent = false } = {}) => {
    if (isDemo) {
      return { items: itemsRef.current, workflows: workflowsRef.current };
    }
    if (refreshInFlightRef.current) {
      // Queue a follow-up so a second workflow becoming ready isn't missed.
      refreshQueuedRef.current = true;
      return refreshInFlightRef.current;
    }

    const request = (async () => {
      if (!silent) {
        setItemsLoading(true);
        setItemsError("");
      }
      try {
        // Workflows land in state as soon as they arrive (refreshWorkflows sets them), so
        // Settings and the source filter never wait on the much larger task-list payload.
        const [itemsData, workflowsData] = await Promise.all([
          getAllActionItems(),
          refreshWorkflows(),
        ]);
        const sorted = [...(Array.isArray(itemsData) ? itemsData : [])].sort(
          compareActionItemsStable
        );
        const workflowList = Array.isArray(workflowsData) ? workflowsData : [];
        setItems((prev) =>
          reuseUnchangedItems(
            prev,
            mergeItemsPreservingPendingMutations(prev, sorted, getProtectedMutationIds())
          )
        );
        setLastFetchedAt(Date.now());
        return { items: sorted, workflows: workflowList };
      } catch (err) {
        const message = String(err?.message || "Failed to load action items");
        if (!silent) {
          setItemsError(message);
        }
        throw err;
      } finally {
        if (!silent) {
          setItemsLoading(false);
        }
      }
    })();

    refreshInFlightRef.current = request;
    try {
      return await request;
    } finally {
      if (refreshInFlightRef.current === request) {
        refreshInFlightRef.current = null;
      }
      if (refreshQueuedRef.current) {
        refreshQueuedRef.current = false;
        void refreshActionItemsData({ silent: true });
      }
    }
  }, [getProtectedMutationIds, isDemo, refreshWorkflows]);

  useEffect(() => {
    if (isDemo) return;
    if (initialFetchRef.current) return;
    initialFetchRef.current = true;
    const hasCache = items.length > 0 || lastFetchedAt != null;
    // Failures are surfaced through itemsError (cold load) or retried by the next poll.
    refreshActionItemsData({ silent: hasCache }).catch(() => {});
  }, [isDemo, items.length, lastFetchedAt, refreshActionItemsData]);

  // One invalidation path for "connector state changed": whichever page changed it
  // (Settings sync/rename/delete, a background poll, the list's own poll), the task list is
  // refetched here, so Settings and the list cannot drift apart. Transition-based, so a
  // steady state never refetches.
  const refreshItemsRef = useRef(refreshActionItemsData);
  refreshItemsRef.current = refreshActionItemsData;
  const workflowSignatureRef = useRef(null);
  useEffect(() => {
    if (isDemo || !workflowsLive) return;
    const next = buildWorkflowChangeSignature(workflows);
    const previous = workflowSignatureRef.current;
    workflowSignatureRef.current = next;
    if (didWorkflowStateChange(previous, next)) {
      void refreshItemsRef.current({ silent: true }).catch(() => {});
    }
  }, [isDemo, workflows, workflowsLive]);

  // Persist the live connector list for instant paint next visit (per user).
  useEffect(() => {
    if (isDemo || !workflowsLive || !userKey) return;
    writeConnectorSnapshot(userKey, { workflows });
  }, [isDemo, userKey, workflows, workflowsLive]);

  /**
   * Drop everything derived from connector state and refetch it. Call after connect,
   * disconnect, rename or sync-now.
   *
   * @example
   * await invalidateConnectors();
   */
  const invalidateConnectors = useCallback(
    () => refreshActionItemsData({ silent: true }),
    [refreshActionItemsData]
  );

  // Desktop app: a hotkey/tray capture lands via Electron's main process, outside
  // any fetch this tab initiated, so the sidebar needs an explicit nudge to pick it up.
  // The deep-link path (same query params as buildActionItemSourceDeepLink) reuses
  // GlobalActionItemsPage's existing deep-link effect to open that item's detail panel.
  useEffect(() => {
    if (isDemo || !window.starmeshDesktop?.onActionItemCaptured) return undefined;
    return window.starmeshDesktop.onActionItemCaptured((payload) => {
      const deepLinkPath = String(payload?.deepLinkPath || "").trim();
      // GlobalActionItemsPage's deep-link effect only gets one shot per URL (it
      // marks the search string "handled" even on a miss), so navigating before
      // the refreshed items (containing this new row) land in state means it
      // matches against the stale list and never retries. Refresh first.
      void refreshActionItemsData({ silent: true }).finally(() => {
        if (deepLinkPath) {
          navigate(deepLinkPath);
        }
      });
    });
  }, [isDemo, refreshActionItemsData, navigate]);

  const registerMutation = useCallback((stableId) => {
    if (!stableId) return;
    pendingMutationIds.current.add(stableId);
    mutationCooldownUntilMs.current.delete(stableId);
  }, []);

  const unregisterMutation = useCallback((stableId) => {
    if (!stableId) return;
    pendingMutationIds.current.delete(stableId);
    mutationCooldownUntilMs.current.set(stableId, Date.now() + MUTATION_COOLDOWN_MS);
  }, []);

  const value = useMemo(
    () => ({
      searchQuery,
      setSearchQuery,
      sourceNav,
      setSourceNav,
      items,
      setItems,
      workflows,
      setWorkflows,
      itemsLoading,
      itemsError,
      lastFetchedAt,
      categoryCatalog,
      setCategoryCatalog,
      refreshActionItemsData,
      refreshWorkflows,
      invalidateConnectors,
      workflowsLive,
      workflowsRefreshing,
      cachedWorkflows: connectorSnapshot?.workflows || null,
      cachedCrmStatusById: connectorSnapshot?.crmStatusById || null,
      registerMutation,
      unregisterMutation,
    }),
    [
      searchQuery,
      sourceNav,
      items,
      workflows,
      itemsLoading,
      itemsError,
      lastFetchedAt,
      categoryCatalog,
      refreshActionItemsData,
      refreshWorkflows,
      invalidateConnectors,
      workflowsLive,
      workflowsRefreshing,
      connectorSnapshot,
      registerMutation,
      unregisterMutation,
    ]
  );

  return (
    <ActionItemsSidebarContext.Provider value={value}>
      {children}
    </ActionItemsSidebarContext.Provider>
  );
};

export default ActionItemsSidebarContext;
